import { create } from "zustand";

export type Staff = {
  id: string;
  name: string;
  role: string;
  department: string;
  /**
   * Người này mặc định CÓ MẶT cả tuần, chỉ khai ngày nghỉ — không đăng ký ca
   * tuần (cờ `brands.ekip_skips_weekly_shift`, migration 0283).
   *
   * Bật khi MỌI thương hiệu đã khai của họ đều bật cờ. Chưa khai thương hiệu
   * nào thì `false`: hàng rỗng trong `staff_brands` nghĩa là CHƯA KHAI, không
   * phải "không thuộc thương hiệu nào".
   *
   * ⚠️ Đây CHỈ là gợi ý hiển thị (màn Đăng ký ca ẩn thẻ "Đăng ký"). Chốt thật
   * ở `POST /ekip-shifts`, đọc lại CSDL trong chính lời gọi ghi — phiên dựng
   * từ ảnh chụp cũ có thể mang cờ cũ. Máy chủ chưa trả trường này thì vắng
   * mặt, và vắng mặt giữ nguyên hành vi đang chạy.
   */
  skipsWeeklyShift?: boolean;
  /**
   * Các phòng giám đốc gán cho người QC này (migration 0331).
   *
   * 🔴 RỖNG (hoặc vắng mặt) nghĩa là CHƯA KHAI — phạm vi đọc tiếp ô
   * `department` như trước, KHÔNG phải "không phụ trách phòng nào". Hiểu
   * ngược là khoá kín màn của mọi người QC hiện tại.
   *
   * ⚠️ Chỉ là gợi ý hiển thị. Lọc thật nằm ở máy chủ (`DeptFilterFor`), và
   * phiên dựng từ ảnh chụp cũ có thể mang danh sách cũ.
   */
  qcDepartments?: string[];
  /**
   * Mã chức danh HRM (`staff.job_title_code`, QĐ 0425). `truong_ca` = Trưởng
   * ca: vai vẫn là `staff`, menu mở thêm trang vận hành của Trưởng cửa hàng
   * (`hasAccess`). Chỉ là gợi ý hiển thị — máy chủ cộng quyền thật mỗi lượt gọi.
   */
  jobTitleCode?: string;
};

/**
 * Một danh phận trong menu "Dùng với tư cách" (QĐ 0255). `id` rỗng = danh
 * phận chính (vai + phòng trên hồ sơ).
 */
export type PersonaOption = {
  id: string;
  role: string;
  department: string;
  label: string;
};

/**
 * Danh phận đang dùng trên MÁY này + danh sách để đổi, do máy chủ trả trong vé
 * đăng nhập. `activeId` rỗng = đang dùng danh phận chính.
 *
 * ⚠️ Chỉ để vẽ menu và dải màu. Vai, phòng, quyền của mọi lời gọi do máy chủ
 * quyết theo danh phận nó nhớ cho máy này; màn không gửi gì để "xin" danh phận.
 */
export type PersonaState = {
  activeId: string;
  primary: PersonaOption;
  personas: PersonaOption[];
};

export type SessionInfo = {
  accessToken?: string | null;
  casdoorSub: string;
  displayName: string;
  staff: Staff | null; // null until Phase 6 backfill links staff.casdoor_sub
  permissions?: string[];
  branchScope?: BranchScope;
  dataVisibility?: DataVisibility;
  /** QĐ 0255. Vắng = cửa không trả (giữ khối đang có); null = không có danh phận. */
  persona?: PersonaState | null | undefined;
};

/**
 * QĐ 0132 (12/09/2026): máy chủ cho biết tài khoản này có được nhận tiền đơn
 * và liên lạc khách hay không (`dataVisibility` trong vé đăng nhập). Cờ này chỉ
 * để trang ẨN Ô thay vì hiện "0 đ" / ô trống — mọi cửa vẫn tự che, trang bỏ
 * qua cờ cũng không thấy được dữ liệu.
 */
export type DataVisibility = {
  /** Tiền gói, cọc, phải thu, phiếu thu, tiền dòng phát sinh của người khác. */
  orderMoney: boolean;
  /** SĐT / thư / địa chỉ khách; tên khách trong dữ liệu khiếu nại. */
  customerContact: boolean;
};

export type BranchScope = {
  source: "database" | "shadow" | "casdoor";
  scopeCodes: string[];
  branches: string[];
  primaryBranch: string;
};

// `scopeCodes` and `branches` are `required` arrays in the OpenAPI contract,
// but a session hydrated from an older snapshot row can still come back null.
// Normalising once here — setSession is the only writer — keeps every consumer
// free to call array methods without a null guard of its own.
function normaliseBranchScope(scope: BranchScope | undefined): BranchScope | undefined {
  if (!scope) return scope;
  return {
    ...scope,
    scopeCodes: scope.scopeCodes ?? [],
    branches: scope.branches ?? [],
  };
}

type State = {
  accessToken: string | null;
  casdoorSub: string | null;
  displayName: string | null;
  currentStaff: Staff | null;
  permissions: Set<string>;
  branchScope: BranchScope | null;
  dataVisibility: DataVisibility | null;
  persona: PersonaState | null;
  /**
   * Câu máy chủ nói khi buộc đăng xuất (QĐ 0412 — nhân sự đã nghỉ). Trang đăng
   * nhập hiện nó; đăng nhập lại được hay tự bấm thoát là xoá.
   */
  signedOutReason: string | null;
  setSession: (s: SessionInfo) => void;
  setAccessToken: (t: string, staff?: Staff | null) => void;
  clear: () => void;
  /** Xoá phiên như `clear`, giữ lại lý do cho trang đăng nhập. */
  signOut: (reason: string) => void;
};

const SIGNED_OUT = {
  accessToken: null,
  casdoorSub: null,
  displayName: null,
  currentStaff: null,
  branchScope: null,
  dataVisibility: null,
  persona: null,
} as const;

// In-memory only. The BE-issued session_id cookie (HttpOnly) carries durable
// identity; access_token is short-lived and re-fetched via /auth/refresh.
// NOTE: Set<string> is intentionally NOT persisted to localStorage — it is
// not JSON-serializable. Permissions are always re-populated from the next
// /auth/refresh response on page load, so persistence is not needed.
export const useAuthV2 = create<State>((set) => ({
  accessToken: null,
  casdoorSub: null,
  displayName: null,
  currentStaff: null,
  permissions: new Set<string>(),
  branchScope: null,
  dataVisibility: null,
  persona: null,
  signedOutReason: null,
  setSession: (s) =>
    set((state) => ({
      accessToken: s.accessToken ?? null,
      casdoorSub: s.casdoorSub,
      displayName: s.displayName,
      currentStaff: s.staff,
      permissions: new Set(s.permissions ?? []),
      branchScope:
        s.branchScope !== undefined
          ? (normaliseBranchScope(s.branchScope) ?? null)
          : state.branchScope,
      // Cửa không trả cờ (đồng bộ hồ sơ) giữ cờ đang có — không vì thế mà mở
      // lại ô tiền cho thợ.
      dataVisibility: s.dataVisibility !== undefined ? s.dataVisibility : state.dataVisibility,
      // Cửa không trả khối danh phận (đồng bộ hồ sơ) giữ khối đang có.
      persona: s.persona !== undefined ? s.persona : state.persona,
      signedOutReason: null,
    })),
  setAccessToken: (t, staff) =>
    set((state) => ({
      accessToken: t,
      currentStaff: staff !== undefined ? staff : state.currentStaff,
    })),
  clear: () => set({ ...SIGNED_OUT, permissions: new Set<string>(), signedOutReason: null }),
  signOut: (reason) => set({ ...SIGNED_OUT, permissions: new Set<string>(), signedOutReason: reason }),
}));
